// src/components/cards/shared/dialogs/convert/ConvertQuoteProspectBody.tsx
//
// Owns the consolidated form state for the prospect convert flow.
// Fetches the full client, sketch pets, reference data, kennel
// side options and availability on open, pre-fills the three
// sections (client, per-pet, kennels), and validates on Convert
// click - setting inline errors and scrolling to the first invalid
// section.

import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { Box } from '@mui/material';
import LoadingSpinner from '../../../../common/LoadingSpinner';
import { useGlobalToast } from '../../../../../App';
import {
  api,
  clientsAPI,
  petsAPI,
  kennelsAPI,
  referenceDataAPI,
  Client,
  Pet,
  Vet,
  PetType,
  PetSize,
  PetBreed,
  PetColour,
  KennelSideOption,
  ConvertQuoteRequest,
  ConvertSketchPetEntry,
} from '../../../../../services/api';
import {
  Kennel,
  KennelSelection,
} from '../../../../forms/helpers/kennelHelpers';
import { VALIDATION_RULES } from '../../../../../utils/validationRules';
import ConvertClientSection, {
  ConvertClientFormState,
  ConvertClientFormErrors,
} from './ConvertClientSection';
import ConvertPetSection, {
  ConvertPetFormState,
  ConvertPetFormErrors,
} from './ConvertPetSection';
import ConvertKennelsSection from './ConvertKennelsSection';

// DOM ids for scroll-to-first-error.
const CLIENT_SECTION_ID = 'convert-quote-client-section';
const KENNELS_SECTION_ID = 'convert-quote-kennels-section';
const petSectionId = (petId: number) => `convert-quote-pet-section-${petId}`;

// Sketch pet identity carried in from the dialog props.
export interface ConvertProspectSketchPet {
  petId: number;
  petName: string;
  petType: string;
  petBreed: string;
}

interface ConvertQuoteProspectBodyProps {
  open: boolean;
  clientId: number;
  checkInDate: string;
  checkOutDate: string;
  numberOfNights: number;
  sketchPets: ConvertProspectSketchPet[];
  isSubmitting: boolean;
  onConfirm: (payload: ConvertQuoteRequest) => void | Promise<void>;
}

// Exposes an imperative submit() so ConvertQuoteDialog's footer Convert
// button - which lives outside this component, in the modal footer - can
// trigger validate+submit directly via a ref, without either component
// needing to know the other's internal state shape.
export interface ConvertQuoteProspectBodyHandle {
  submit: () => void;
}

const ConvertQuoteProspectBody = forwardRef<
  ConvertQuoteProspectBodyHandle,
  ConvertQuoteProspectBodyProps
>(function ConvertQuoteProspectBody(
  {
    open,
    clientId,
    checkInDate,
    checkOutDate,
    numberOfNights,
    sketchPets,
    isSubmitting,
    onConfirm,
  },
  ref
) {
  const { showError } = useGlobalToast();

  // The load effect must not re-run (and wipe the user's picks) just
  // because the parent passed a new array or toast function identity.
  const sketchPetsRef = useRef(sketchPets);
  const showErrorRef = useRef(showError);
  useEffect(() => {
    sketchPetsRef.current = sketchPets;
    showErrorRef.current = showError;
  });
  const sketchPetIdsKey = sketchPets.map((sp) => sp.petId).join(',');

  const [loading, setLoading] = useState<boolean>(true);
  const [availableVets, setAvailableVets] = useState<Vet[]>([]);
  const [petTypes, setPetTypes] = useState<PetType[]>([]);
  const [petSizes, setPetSizes] = useState<PetSize[]>([]);
  const [petBreeds, setPetBreeds] = useState<PetBreed[]>([]);
  const [petColours, setPetColours] = useState<PetColour[]>([]);
  const [kennelSideOptions, setKennelSideOptions] = useState<
    KennelSideOption[]
  >([]);
  const [availableKennels, setAvailableKennels] = useState<Kennel[]>([]);

  const [clientForm, setClientForm] = useState<ConvertClientFormState>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    vetId: '',
    idNumber: '',
    alternateContact: '',
    alternateContactOther: '',
  });
  const [clientErrors, setClientErrors] = useState<ConvertClientFormErrors>({});

  const [petForms, setPetForms] = useState<Record<number, ConvertPetFormState>>(
    {}
  );
  const [petErrors, setPetErrors] = useState<
    Record<number, ConvertPetFormErrors>
  >({});

  const [kennelSelections, setKennelSelections] = useState<KennelSelection[]>([
    {
      id: 1,
      kennelSideOptionId: null,
      kennelSideId: null,
      size: null,
      kennelId: null,
      heaterEnabled: false,
      heaterNights: 0,
    },
  ]);
  const [kennelsError, setKennelsError] = useState<string>('');

  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    setLoading(true);

    setClientErrors({});
    setPetErrors({});
    setKennelsError('');
    setKennelSelections([
      {
        id: 1,
        kennelSideOptionId: null,
        kennelSideId: null,
        size: null,
        kennelId: null,
        heaterEnabled: false,
        heaterNights: 0,
      },
    ]);

    const loadAll = async () => {
      try {
        const [
          client,
          allPets,
          vets,
          types,
          sizes,
          breeds,
          colours,
          sideOptions,
          availability,
        ] = await Promise.all([
          clientsAPI.getById(clientId),
          petsAPI.getByClient(clientId),
          referenceDataAPI.getVets(),
          referenceDataAPI.getPetTypes(),
          referenceDataAPI.getPetSizes(),
          referenceDataAPI.getPetBreeds(),
          referenceDataAPI.getPetColours(),
          kennelsAPI.getSideOptions(),
          api.post<{ availableKennels: Kennel[] }>(
            '/Booking/CheckAvailability',
            { checkInDate, checkOutDate }
          ),
        ]);

        if (cancelled) return;

        setAvailableVets(vets);
        setPetTypes(types);
        setPetSizes(sizes);
        setPetBreeds(breeds);
        setPetColours(colours);
        setKennelSideOptions(sideOptions);
        setAvailableKennels(availability.data.availableKennels);

        setClientForm({
          firstName: client.firstName ?? '',
          lastName: client.lastName ?? '',
          email: client.email ?? '',
          phone: client.phone ?? '',
          address: client.address ?? '',
          vetId: client.vetId ? client.vetId.toString() : '',
          idNumber: client.idNumber ?? '',
          alternateContact: client.alternateContact ?? '',
          alternateContactOther: client.alternateContactOther ?? '',
        });

        const currentSketchPets = sketchPetsRef.current;
        const sketchPetIds = new Set(currentSketchPets.map((sp) => sp.petId));
        const sketchPetRecords = allPets.filter((p) => sketchPetIds.has(p.id));

        const nextPetForms: Record<number, ConvertPetFormState> = {};
        currentSketchPets.forEach((sp) => {
          const full = sketchPetRecords.find((p) => p.id === sp.petId);
          nextPetForms[sp.petId] = {
            name: sp.petName ?? full?.name ?? '',
            petTypeId: full?.petTypeId ? full.petTypeId.toString() : '',
            petSizeId: full?.petSizeId ? full.petSizeId.toString() : '',
            petBreedId: full?.petBreedId ? full.petBreedId.toString() : '',
            petColourId: full?.petColourId ? full.petColourId.toString() : '',
            gender: full?.gender ?? '',
            dateOfBirth: full?.dateOfBirth
              ? full.dateOfBirth.split('T')[0]
              : '',
            inoculationDate: full?.inoculationDate
              ? full.inoculationDate.split('T')[0]
              : '',
            sterilised: full?.sterilised ?? false,
            stillOwned: full?.stillOwned ?? true,
          };
        });
        setPetForms(nextPetForms);
      } catch (err) {
        if (cancelled) return;
        showErrorRef.current(
          'Failed to load conversion data. Please try again.'
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadAll();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, clientId, checkInDate, checkOutDate, sketchPetIdsKey]);

  // Validates the form, optionally committing inline errors. Returns
  // the DOM id of the first invalid section (client -> pets -> kennels),
  // or null if valid.
  const validate = useCallback(
    (commit: boolean): string | null => {
      const cErr: ConvertClientFormErrors = {};
      if (!VALIDATION_RULES.isRequired(clientForm.firstName))
        cErr.firstName = 'First name is required';
      if (!VALIDATION_RULES.isRequired(clientForm.lastName))
        cErr.lastName = 'Last name is required';
      if (!VALIDATION_RULES.isRequired(clientForm.phone))
        cErr.phone = 'Phone is required';
      if (
        VALIDATION_RULES.isRequired(clientForm.email) &&
        !VALIDATION_RULES.isValidEmail(clientForm.email)
      ) {
        cErr.email = 'Email is invalid';
      }

      const pErr: Record<number, ConvertPetFormErrors> = {};
      let firstInvalidPetId: number | null = null;
      sketchPets.forEach((sp) => {
        const pf = petForms[sp.petId];
        if (!pf) {
          if (firstInvalidPetId === null) firstInvalidPetId = sp.petId;
          return;
        }
        const e: ConvertPetFormErrors = {};
        if (!VALIDATION_RULES.isRequired(pf.name))
          e.name = 'Pet name is required';
        if (!pf.petTypeId) e.petTypeId = 'Type is required';
        if (!pf.petSizeId) e.petSizeId = 'Size is required';
        if (!VALIDATION_RULES.isRequired(pf.gender))
          e.gender = 'Sex is required';
        if (Object.keys(e).length > 0) {
          pErr[sp.petId] = e;
          if (firstInvalidPetId === null) firstInvalidPetId = sp.petId;
        }
      });

      const hasKennel = kennelSelections.some(
        (s) => s.kennelId !== null && s.kennelId !== undefined
      );
      const kErr = hasKennel ? '' : 'At least one kennel must be selected';

      if (commit) {
        setClientErrors(cErr);
        setPetErrors(pErr);
        setKennelsError(kErr);
      }

      if (Object.keys(cErr).length > 0) return CLIENT_SECTION_ID;
      if (firstInvalidPetId !== null) return petSectionId(firstInvalidPetId);
      if (kErr) return KENNELS_SECTION_ID;
      return null;
    },
    [clientForm, petForms, sketchPets, kennelSelections]
  );

  const buildPayload = (): ConvertQuoteRequest => {
    const petsPayload: ConvertSketchPetEntry[] = sketchPets.map((sp) => {
      const pf = petForms[sp.petId];
      const breedId = pf.petBreedId ? parseInt(pf.petBreedId, 10) : undefined;
      const colourId = pf.petColourId
        ? parseInt(pf.petColourId, 10)
        : undefined;
      return {
        petId: sp.petId,
        name: pf.name.trim(),
        petTypeId: parseInt(pf.petTypeId, 10),
        petSizeId: parseInt(pf.petSizeId, 10),
        petBreedId: breedId ?? null,
        petColourId: colourId ?? null,
        gender: pf.gender,
        dateOfBirth: pf.dateOfBirth || null,
        inoculationDate: pf.inoculationDate || null,
        sterilised: pf.sterilised,
        stillOwned: pf.stillOwned,
      };
    });

    return {
      pets: petsPayload,
      clientPatch: {
        firstName: clientForm.firstName.trim(),
        lastName: clientForm.lastName.trim(),
        phone: clientForm.phone.trim(),
        email: clientForm.email.trim() || undefined,
        address: clientForm.address.trim() || undefined,
        vetId: clientForm.vetId ? parseInt(clientForm.vetId, 10) : null,
        idNumber: clientForm.idNumber.trim() || undefined,
        alternateContact: clientForm.alternateContact.trim() || undefined,
        alternateContactOther:
          clientForm.alternateContactOther.trim() || undefined,
      },
      kennels: kennelSelections
        .filter((s) => s.kennelId !== null && s.kennelId !== undefined)
        .map((s) => ({
          kennelId: s.kennelId as number,
          heaterNights: s.heaterEnabled && s.heaterNights ? s.heaterNights : 0,
        })),
    };
  };

  const handleAttemptSubmit = () => {
    if (loading) return;
    const firstInvalidSectionId = validate(true);
    if (firstInvalidSectionId) {
      // Defer to let error text/borders render before scrolling.
      requestAnimationFrame(() => {
        document
          .getElementById(firstInvalidSectionId)
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
      return;
    }
    void onConfirm(buildPayload());
  };

  // Re-assigning ref.current on every render is the normal, idiomatic use
  // of useImperativeHandle (unlike a DOM/window event listener, there's no
  // add/remove churn or registration gap - it's a plain property
  // assignment) - so ConvertQuoteDialog's ref.current.submit() always
  // calls the latest handleAttemptSubmit closure.
  useImperativeHandle(ref, () => ({
    submit: handleAttemptSubmit,
  }));

  const petTypeIds = Array.from(
    new Set(
      sketchPets
        .map((sp) => parseInt(petForms[sp.petId]?.petTypeId ?? '', 10))
        .filter((id) => !Number.isNaN(id))
    )
  );

  // --- Render --------------------------------------------------------
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <LoadingSpinner size={80} message="Loading conversion data..." />
      </Box>
    );
  }

  return (
    <Box>
      <ConvertClientSection
        id={CLIENT_SECTION_ID}
        value={clientForm}
        errors={clientErrors}
        availableVets={availableVets}
        isSubmitting={isSubmitting}
        onChange={(next) => {
          setClientForm(next);
          if (Object.keys(clientErrors).length > 0) setClientErrors({});
        }}
      />

      {sketchPets.map((sp) => {
        const pf = petForms[sp.petId];
        if (!pf) return null;
        return (
          <ConvertPetSection
            key={sp.petId}
            id={petSectionId(sp.petId)}
            petId={sp.petId}
            originalSketchName={sp.petName}
            originalPetTypeName={sp.petType}
            value={pf}
            errors={petErrors[sp.petId] ?? {}}
            isSubmitting={isSubmitting}
            petTypes={petTypes}
            petSizes={petSizes}
            petBreeds={petBreeds}
            petColours={petColours}
            onChange={(next) => {
              setPetForms((prev) => ({ ...prev, [sp.petId]: next }));
              if (petErrors[sp.petId]) {
                setPetErrors((prev) => {
                  const cp = { ...prev };
                  delete cp[sp.petId];
                  return cp;
                });
              }
            }}
          />
        );
      })}

      <ConvertKennelsSection
        id={KENNELS_SECTION_ID}
        kennelSelections={kennelSelections}
        availableKennels={availableKennels}
        kennelSideOptions={kennelSideOptions}
        numberOfDays={numberOfNights}
        isSubmitting={isSubmitting}
        petTypeIds={petTypeIds}
        error={kennelsError}
        onChange={(next) => {
          setKennelSelections(next);
          if (kennelsError) setKennelsError('');
        }}
      />
    </Box>
  );
});

export default ConvertQuoteProspectBody;
